
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, Star, Rocket, CheckCircle2, Bell } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export interface Notification {
  id: string;
  title: string;
  description: string;
  time: string;
  type: "inquiry" | "featured" | "boost" | "system";
  read: boolean;
}

interface NotificationsContentProps {
  notifications: Notification[];
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
}

const NotificationsContent = ({ 
  notifications, 
  onMarkAsRead = () => {}, 
  onMarkAllAsRead = () => {} 
}: NotificationsContentProps) => {
  const handleMarkAllAsRead = () => {
    onMarkAllAsRead();
    toast.success("All notifications marked as read");
  };

  // Function to get the icon based on notification type
  const getIcon = (type: string) => {
    switch(type) {
      case 'inquiry':
        return <MessageSquare className="h-5 w-5 text-blue-600" />;
      case 'featured':
        return <Star className="h-5 w-5 text-amber-600" />;
      case 'boost':
        return <Rocket className="h-5 w-5 text-sky-600" />;
      case 'system':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      default:
        return <Bell className="h-5 w-5 text-gray-600" />;
    }
  };

  // Function to get background color based on notification type
  const getBackgroundColor = (type: string) => {
    switch(type) {
      case 'inquiry':
        return 'bg-blue-100';
      case 'featured':
        return 'bg-amber-100';
      case 'boost':
        return 'bg-sky-100';
      case 'system':
        return 'bg-green-100';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Notifications</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleMarkAllAsRead}
          disabled={notifications.every(n => n.read)}
        >
          Mark all as read
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {notifications.length === 0 && (
            <div className="text-center py-8">
              <Bell className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-muted-foreground">No notifications</p>
            </div>
          )}
          
          {notifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`flex gap-4 pb-4 last:pb-0 border-b last:border-0 border-gray-100 ${!notification.read ? 'bg-gray-50 p-2 rounded-md' : ''}`}
              onClick={() => onMarkAsRead(notification.id)}
            >
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                ${getBackgroundColor(notification.type)}
              `}>
                {getIcon(notification.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-medium">
                    {notification.title}
                  </div>
                  {!notification.read && (
                    <div className="h-2 w-2 bg-red-500 rounded-full"></div>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">{notification.description}</div>
                <div className="text-xs text-muted-foreground mt-1">{notification.time}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter>
        <Button variant="outline" className="w-full" asChild>
          <Link to="/dashboard/notifications">View All Notifications</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NotificationsContent;
